import React, { useState, useEffect, useContext } from 'react';
import SEOHead from './SEOHead';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { IndianRupee, ArrowLeft, ShieldCheck, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';
import Footer from './Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const EventCheckout = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.info('Please sign in to continue');
      openAuth('login');
      navigate('/events');
      return;
    }
    fetchEvent();
    // eslint-disable-next-line
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvent(res.data.event);
    } catch (e) {
      toast.error('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handlePay = async () => {
    if (isMobileApp()) {
      openWebappWithAuth(`/events/${eventId}`);
      return;
    }
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const orderRes = await axios.post(`${API}/api/events/${eventId}/create-order`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { order_id, amount, currency, key_id, total_amount, event_title } = orderRes.data;

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load Razorpay');
        setProcessing(false);
        return;
      }

      const options = {
        key: key_id,
        amount,
        currency,
        name: 'Nerd Nation',
        description: event_title,
        order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/api/events/${eventId}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, {
              headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(`Registered for ${event_title} 🎉`);
            navigate('/my-events');
          } catch (e) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setProcessing(false);
          }
        },
        prefill: {
          name: user.full_name || '',
          email: user.email || '',
          contact: user.profile?.mobile_number || '',
        },
        theme: { color: '#f97316' },
        modal: {
          ondismiss: () => setProcessing(false),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start payment');
      setProcessing(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  if (!event) return null;
  if (event.event_type === 'free') {
    navigate(`/events/${eventId}`);
    return null;
  }

  const base = event.fee || 0;
  const gst = Math.round(base * 0.18);
  const total = base + gst;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <SEOHead title={`Checkout — ${event.title}`} description="Complete event registration on Nerd Nation." path={`/event-checkout/${eventId}`} noIndex={true} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button onClick={() => navigate(`/events/${eventId}`)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to event
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Complete your registration</h1>
        <p className="text-sm text-gray-600 mb-6">Secure your spot for this event</p>

        <Card className="border-2 border-orange-200 shadow-lg">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start gap-3 pb-4 border-b border-gray-100">
              <div className="p-2 rounded-lg bg-orange-100">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">{event.title}</div>
                {event.event_date && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {new Date(event.event_date).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-700">
                <span>Base fee</span>
                <span className="font-medium">₹{base.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>GST (18%)</span>
                <span className="font-medium">₹{gst.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span className="flex items-center"><IndianRupee className="h-4 w-4" />{total.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <Button
              data-testid="event-pay-now-btn"
              onClick={handlePay}
              disabled={processing}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
            >
              {processing ? 'Processing…' : `Pay ₹${total.toLocaleString('en-IN')} via Razorpay`}
            </Button>
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500">
              <ShieldCheck className="h-3.5 w-3.5" /> Secured by Razorpay · 256-bit encryption
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default EventCheckout;
